import { useState } from "react";
import { RotateCcw, Maximize2, X, Upload, FileText, Image as ImageIcon, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, PrimaryButton } from "@/components/site/Bits";
import { Reveal } from "@/components/site/Reveal";
import { FichePreview } from "@/components/site/FichePreview";
import { DemoCropper } from "@/components/site/DemoCropper";
import { useI18n } from "@/i18n/I18nContext";

const INITIAL_DEMO = {
  first_name: "Julie", last_name: "Moreau", job_title: "Conseillère immobilière", company_name: "ACI Immobilier",
  phone: "+33 6 12 34 56 78", email: "julie.moreau@aci-immobilier.fr", website: "www.aci-immobilier.fr",
  address: "12 rue de la République, 69002 Lyon",
  description: "Spécialiste de l'immobilier résidentiel sur Lyon depuis 8 ans.",
  linkedin: "https://linkedin.com/in/juliemoreau", whatsapp: "+33612345678",
  instagram: "https://instagram.com/aci.immo", facebook: "", tiktok: "", youtube: "",
  google_reviews: "https://g.page/r/aci-immobilier/review", pdf_url: "", pdf_name: "",
  profile_photo_url: "", cover_url: "",
  primary_color: "#06B6D4", border_color: "#2563EB",
};

const CATS = [
  { key: "catIdentity", fields: [["first", "first_name"], ["last", "last_name"], ["job", "job_title"], ["company", "company_name"]] },
  { key: "catContact", fields: [["phone", "phone"], ["email", "email"], ["website", "website"], ["address", "address"]] },
  { key: "catSocial", fields: [["l_linkedin", "linkedin"], ["l_whatsapp", "whatsapp"], ["l_instagram", "instagram"], ["l_facebook", "facebook"], ["l_tiktok", "tiktok"], ["l_youtube", "youtube"]] },
];

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm placeholder:text-zinc-600 focus-ring" />
    </div>
  );
}

export default function DigitalCards() {
  const { t } = useI18n();
  const [d, setD] = useState(INITIAL_DEMO);
  const [fullscreen, setFullscreen] = useState(false);
  const [crop, setCrop] = useState(null); // { src, target, aspect }
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v }));

  const onImage = (target, aspect) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCrop({ src: URL.createObjectURL(file), target, aspect });
    e.target.value = "";
  };
  const onPdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setD((p) => ({ ...p, pdf_url: URL.createObjectURL(file), pdf_name: file.name }));
    e.target.value = "";
  };
  const reset = () => setD(INITIAL_DEMO);

  const LABELS = {
    first: t("demo.first"), last: t("demo.last"), job: t("demo.job"), company: t("demo.company"),
    phone: t("demo.phone"), email: t("demo.email"), website: t("demo.website"), address: t("demo.address"),
    l_linkedin: "LinkedIn", l_whatsapp: "WhatsApp", l_instagram: "Instagram", l_facebook: "Facebook", l_tiktok: "TikTok", l_youtube: "YouTube",
  };

  return (
    <div>
      <PageHero eyebrow={t("digital.eyebrow")} title={t("digital.title")} subtitle={t("digital.subtitle")} />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start" data-testid="demo-configurator">
        {/* LEFT — phone preview */}
        <Reveal className="lg:sticky lg:top-24">
          <div className="mx-auto max-w-[360px]">
            <div className="relative rounded-[2.6rem] border border-white/10 bg-[#08080f] p-3 shadow-2xl">
              <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-[#08080f] rounded-b-2xl z-10" />
              <div className="rounded-[2rem] overflow-hidden max-h-[640px] overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <FichePreview d={d} />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-3">
              <button onClick={() => setFullscreen(true)} data-testid="demo-fullscreen"
                className="inline-flex items-center gap-2 glass text-white text-sm px-4 py-2.5 rounded-full hover:bg-white/10 transition-colors duration-300">
                <Maximize2 className="w-4 h-4" /> {t("demo.fullscreen")}
              </button>
              <button onClick={reset} data-testid="demo-reset"
                className="inline-flex items-center gap-2 glass text-zinc-300 text-sm px-4 py-2.5 rounded-full hover:bg-white/10 transition-colors duration-300">
                <RotateCcw className="w-4 h-4" /> {t("demo.reset")}
              </button>
            </div>
          </div>
        </Reveal>

        {/* RIGHT — configurator form */}
        <Reveal delay={0.1}>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white">{t("demo.title")}</h2>
          <p className="mt-3 text-zinc-400">{t("demo.subtitle")}</p>
          <p className="mt-3 text-xs text-cyan-400/80">{t("demo.notice")}</p>

          <div className="mt-8 space-y-7">
            {CATS.map((cat) => (
              <div key={cat.key}>
                <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{t(`demo.${cat.key}`)}</h3>
                <div className="mt-3 grid sm:grid-cols-2 gap-3.5">
                  {cat.fields.map(([lk, dk]) => (
                    <Field key={dk} label={LABELS[lk]} value={d[dk]} onChange={set(dk)} testid={`demo-field-${dk}`} />
                  ))}
                </div>
              </div>
            ))}

            {/* CONTENT */}
            <div>
              <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{t("demo.catContent")}</h3>
              <div className="mt-3 space-y-3.5">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">{t("demo.description")}</label>
                  <textarea rows={3} value={d.description || ""} onChange={(e) => set("description")(e.target.value)} data-testid="demo-field-description"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-white text-sm focus-ring" />
                </div>
                <Field label={t("demo.reviews")} value={d.google_reviews} onChange={set("google_reviews")} testid="demo-field-google_reviews" />
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">{t("demo.pdf")}</label>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="demo-pdf-upload">
                      <FileText className="w-4 h-4" /> {t("demo.choosePdf")}
                      <input type="file" accept="application/pdf" className="hidden" onChange={onPdf} />
                    </label>
                    {d.pdf_url && (
                      <>
                        <span className="text-sm text-zinc-400 truncate max-w-[140px]">{d.pdf_name}</span>
                        <button onClick={() => setD((p) => ({ ...p, pdf_url: "", pdf_name: "" }))} className="text-sm text-red-400">{t("demo.remove")}</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* CUSTOMIZATION */}
            <div>
              <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">{t("demo.catCustom")}</h3>
              <div className="mt-3 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-zinc-500 mb-2 block">{t("demo.photo")}</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                      {d.profile_photo_url ? <img src={d.profile_photo_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-zinc-600" />}
                    </div>
                    <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="demo-photo-upload">
                      <Upload className="w-4 h-4" /> {t("demo.import")}
                      <input type="file" accept="image/*" className="hidden" onChange={onImage("profile_photo_url", 1)} />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-2 block">{t("demo.cover")}</label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-14 rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-center justify-center">
                      {d.cover_url ? <img src={d.cover_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-zinc-600" />}
                    </div>
                    <label className="inline-flex items-center gap-2 glass px-4 py-2.5 rounded-xl text-sm text-white cursor-pointer hover:bg-white/10 transition-colors duration-300" data-testid="demo-cover-upload">
                      <Upload className="w-4 h-4" /> {t("demo.import")}
                      <input type="file" accept="image/*" className="hidden" onChange={onImage("cover_url", 16 / 9)} />
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-2 block">{t("demo.primaryColor")}</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={d.primary_color} onChange={(e) => set("primary_color")(e.target.value)} data-testid="demo-primary-color" className="w-11 h-11 rounded-lg bg-transparent cursor-pointer" />
                    <span className="font-mono text-sm text-zinc-400">{d.primary_color}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-2 block">{t("demo.borderColor")}</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={d.border_color} onChange={(e) => set("border_color")(e.target.value)} data-testid="demo-border-color" className="w-11 h-11 rounded-lg bg-transparent cursor-pointer" />
                    <span className="font-mono text-sm text-zinc-400">{d.border_color}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-9">
            <Link to="/contact" data-testid="demo-create-cta"
              className="inline-flex items-center gap-2 bg-brand-gradient text-white font-semibold px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity duration-300">
              {t("demo.createCard")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      {crop && (
        <DemoCropper src={crop.src} aspect={crop.aspect}
          onCancel={() => setCrop(null)}
          onDone={(url) => { set(crop.target)(url); setCrop(null); }} />
      )}

      {fullscreen && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="demo-fullscreen-modal" onClick={() => setFullscreen(false)}>
          <div className="w-full max-w-md my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setFullscreen(false)} data-testid="demo-fullscreen-close" className="glass text-white p-2 rounded-full hover:bg-white/10 transition-colors duration-300"><X className="w-5 h-5" /></button>
            </div>
            <FichePreview d={d} />
          </div>
        </div>
      )}
    </div>
  );
}
